import { useCollection } from "@/composables/useFirestore";
import { useTrackedCategoryGroups } from "@/composables/useTrackedCategoryGroups";
import { useTransactions } from "@/composables/useTransactions";
import { useTransferRequests } from "@/composables/useTransferRequests";
import { db } from "@/firebase";
import type { TrackedCategory, TrackedCategoryDirection } from "@aircap/common/trackedCategory";
import {
  getTrackedCategorySignedAllocations,
  isFailedSwanTransactionStatus,
  realizedAmountFromTransferRequest,
  type TransferBudgetOutcome,
} from "@aircap/common/trackedCategory";
import { treezorOperationsFullPath, type Operation } from "@aircap/common/treezor";
import { collection, query, where } from "firebase/firestore";
import { computed, type Ref } from "vue";

export function useTrackedCategories(accountId: Ref<string>, direction: TrackedCategoryDirection) {
  // Expenses are stored negative, revenues positive — flip so "réalisé" is positive.
  const displaySign = direction === "outgoing" ? -1 : 1;

  const { groups } = useTrackedCategoryGroups(accountId, direction);

  const categoriesSource = computed(() =>
    collection<TrackedCategory>(db, `accounts/${accountId.value}/trackedCategories`),
  );

  const { data: categoriesRaw, pending: loading } =
    useCollection<TrackedCategory>(categoriesSource);

  const { transactions } = useTransactions(accountId);
  const { transferRequests, account } = useTransferRequests(accountId);

  const failedTreezorOperationsSource = computed(() => {
    if (account.value?.accountType !== "treezor") return undefined;
    return query(
      collection<Operation>(db, treezorOperationsFullPath(accountId.value)),
      where<Operation>("status", "in", ["CANCELED", "DECLINED"]),
    );
  });
  const { data: failedTreezorOperations } = useCollection<Operation>(failedTreezorOperationsSource);

  const netAmountByCategory = computed(() => {
    const totals = new Map<string, number>();
    for (const transaction of transactions.value ?? []) {
      if (transaction.is_future) continue;
      for (const allocation of getTrackedCategorySignedAllocations(transaction)) {
        totals.set(
          allocation.trackedCategoryId,
          (totals.get(allocation.trackedCategoryId) ?? 0) + allocation.amount,
        );
      }
    }
    return totals;
  });

  const transferAmountByCategory = computed(() => {
    const totals = new Map<string, number>();
    if (direction !== "outgoing") return totals;

    const bookedTransferRequestIds = new Set<string>();
    const categorizedTransactionIds = new Set<string>();
    for (const transaction of transactions.value ?? []) {
      if (transaction.is_future) continue;
      if (transaction.transferRequest) bookedTransferRequestIds.add(transaction.transferRequest.id);
      if ((transaction.trackedCategorySplits?.length ?? 0) > 0)
        categorizedTransactionIds.add(transaction.id);
    }

    const failedTransferRequestIds = new Set<string>();
    const failedTreezorOperationIds = new Set<string>();
    for (const operation of failedTreezorOperations.value ?? []) {
      failedTreezorOperationIds.add(operation.objectId);
      if (operation.transferRequest) failedTransferRequestIds.add(operation.transferRequest.id);
    }

    for (const request of transferRequests.value ?? []) {
      if (!request.trackedCategoryId) continue;
      let outcome: TransferBudgetOutcome = "unsettled";
      if (
        bookedTransferRequestIds.has(request.id) ||
        (request.transactionId !== undefined &&
          categorizedTransactionIds.has(request.transactionId))
      )
        outcome = "booked";
      else if (
        failedTransferRequestIds.has(request.id) ||
        (request.transactionId !== undefined &&
          failedTreezorOperationIds.has(request.transactionId)) ||
        isFailedSwanTransactionStatus(request.linkedTransactionStatus)
      )
        outcome = "failed";
      const amount = realizedAmountFromTransferRequest(request, outcome);
      if (amount === 0) continue;
      totals.set(request.trackedCategoryId, (totals.get(request.trackedCategoryId) ?? 0) + amount);
    }
    return totals;
  });

  const categories = computed(() => {
    if (!categoriesRaw.value) return [];

    /** Order of groups matches ManageCategoriesDialog (sorted by group order). */
    const groupPosition = new Map(groups.value.map((g, index) => [g.id, index]));
    const ungroupedPosition = groups.value.length;

    const positionInTree = (groupId: string | undefined) => {
      if (!groupId || !groupPosition.has(groupId)) return ungroupedPosition;
      return groupPosition.get(groupId) ?? ungroupedPosition;
    };

    return categoriesRaw.value
      .filter((category) => !category.deletedAt && category.direction === direction)
      .map((category) => {
        const fromTransactions = (netAmountByCategory.value.get(category.id) ?? 0) * displaySign;
        const fromTransferRequests =
          direction === "outgoing" ? (transferAmountByCategory.value.get(category.id) ?? 0) : 0;
        return {
          id: category.id,
          name: category.name,
          amount: category.amount,
          initialAmount: category.initialAmount,
          direction: category.direction,
          groupId: category.groupId,
          order: category.order,
          completedAt: category.completedAt,
          createdAt: category.createdAt,
          updatedAt: category.updatedAt,
          currentAmount:
            fromTransactions +
            fromTransferRequests +
            (typeof category.initialAmount === "number" && Number.isFinite(category.initialAmount)
              ? category.initialAmount
              : 0),
        };
      })
      .sort((a, b) => {
        const ga = positionInTree(a.groupId);
        const gb = positionInTree(b.groupId);
        if (ga !== gb) return ga - gb;

        const ao = a.order ?? Infinity;
        const bo = b.order ?? Infinity;
        if (ao !== bo) return ao - bo;
        const at = a.createdAt?.toMillis?.() ?? 0;
        const bt = b.createdAt?.toMillis?.() ?? 0;
        return at - bt;
      });
  });

  const hasCategories = computed(() => categories.value.length > 0);

  return {
    categories,
    groups,
    hasCategories,
    loading,
  };
}
